import type { ReactNode } from 'react';
import { ArrowRight, EyeOff, type LucideIcon } from '../../../icons';
import { useWidgetChrome } from './widget-chrome';

/**
 * 위젯 카드 셸 — 모든 위젯이 공유하는 껍데기.
 *
 * ── 2026-09-08 · 시안 화면 A·F 「머리 없는 카드」로 갈아 끼웠다 ────────────────────
 * 종전에는 카드마다 **머리 띠**(선 + 아이콘 + 제목 + 도구)가 있었다. 위젯이 여섯일 때는
 * 괜찮았지만 열넷이 되니 화면의 3분의 1이 띠와 선이 됐다. 시안이 정한 것:
 *
 *   ★★★ **제목만 본문 위에 얹는다**(띠도 선도 없다). 그림이 주인공인 위젯(날씨 · 방송 ·
 *       공지 슬라이드)은 `flush` 로 여백까지 없애 카드를 통으로 쓴다.
 *   ★★★ **도구(손잡이 · 설정 · ⋯)는 편집 중에만 나타난다** — 보기 상태의 화면이 조용해진다.
 *       도구는 여기서 그리지 않는다. 상위 `WidgetGrid` 의 프레임이 카드 위에 겹쳐 얹는다
 *       (위젯 14종이 도구를 받아 넘기지 않아도 되도록).
 *
 * ⚠ **머리 띠에 있던 것 둘은 «없앤» 것이 아니라 «옮긴» 것이다** — 안 옮기면 회귀다:
 *   · `tail`(건수 · 기간) → 제목 칩 뒤에 붙는다.
 *   · `link`(더보기 · 메일함 …) → **본문 아래 한 줄**로 내려간다(시안 화면 M 의 「메일함 열기」).
 *
 * 표시 이름 · 제목 표시 · 높이는 **그 자리에 놓인 위젯**의 값이라 context 로 온다(`widget-chrome`).
 */

export interface WidgetCardProps {
  title: string;
  icon?: LucideIcon;
  /** 제목 뒤 보조 표기 (건수·기간 등). */
  tail?: ReactNode;
  /** 본문 아래로 내려가는 이동 링크. */
  link?: { label: string; onClick: () => void };
  loading?: boolean;
  error?: string | null;
  /** 값이 있으면 본문 대신 이 문구를 빈 상태로 표시. */
  empty?: string | null;
  /**
   * 메뉴 관리에서 이 위젯의 메뉴를 껐을 때 넘기는 **메뉴 이름**(2026-08-11).
   * 값이 있으면 로딩·오류·본문을 모두 제치고 "메뉴 관리에서 사용을 켜세요" 안내만 표시한다.
   */
  menuOff?: string | null;
  /** 본문 여백 제거 — 그림이 주인공인 위젯(날씨 · 방송 · 슬라이드)용. */
  flush?: boolean;
  children?: ReactNode;
}

export function WidgetCard({
  title, icon: Icon, tail, link, loading = false, error = null, empty = null,
  menuOff = null, flush = false, children,
}: WidgetCardProps) {
  const chrome = useWidgetChrome();
  const state = loading ? '로딩…' : error ? '조회 실패' : empty;
  const shownTitle = chrome.title?.trim() || title;

  return (
    <article
      className={`dwh-card${flush ? ' dwh-card--flush' : ''}`}
      data-h={chrome.height}
    >
      {chrome.showTitle && (
        <span className="dwh-title">
          {Icon && <Icon size={13} aria-hidden />}
          {shownTitle}
          {tail != null && !menuOff && <b className="dwh-title__tail">{tail}</b>}
        </span>
      )}

      <div className={`dwh-body${flush && !menuOff ? ' dwh-body--flush' : ''}${chrome.showTitle ? '' : ' dwh-body--notitle'}`}>
        {menuOff
          ? (
            <div className="dw-placeholder dw-placeholder--off">
              <EyeOff size={22} aria-hidden />
              <div className="dw-placeholder__t">{menuOff} 메뉴를 사용하지 않습니다</div>
              <div className="dw-placeholder__s">관리 콘솔 &gt; 메뉴 관리에서 사용을 켜면 표시됩니다</div>
            </div>
          )
          : state != null
            ? <div className="dw-card__state">{state}</div>
            : children}
      </div>

      {/* 머리 띠에 있던 이동 링크 — 없애지 않고 본문 아래로 내린다. */}
      {link && !menuOff && state == null && (
        <button type="button" className="dwh-more" onClick={link.onClick}>
          {link.label}
          <ArrowRight size={12} aria-hidden />
        </button>
      )}
    </article>
  );
}
